import React, { useEffect, useState } from 'react';

import { getAllModificationsByLocalIdAndRangeDate, getAllModificationsByLocalIdAndSessionId } from '../../../../../../crud/pedidos_api/pedidos_modifications';
import MultiSelectFilter from '../../../../../../components/common/MultiSelectFilter/MultiSelectFilter';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import { PedidoModification } from '../../Types';
import { getMesasOptions, getUsersOptions, UsersOption, MesasOption } from '../../utils/utils';
import { Button, Offcanvas, Form, InputGroup } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import DateFilter from '../../../../Caja/Receipts/components/Filters/DateFilter';

interface FilterProps {
    show: boolean;
    setShow: (show: boolean) => void;
    setFilteredModifications: (modifications: PedidoModification[]) => void;
    setStartDate: (date: string) => void;
    setEndDate: (date: string) => void;
    startDate: string;
    endDate: string;
    selectedUsers: any[];
    setSelectedUsers: (users: any[]) => void;
    selectedMesas: any[];
    setSelectedMesas: (mesas: any[]) => void;
    soloSesionActual: boolean;
    setSoloSesionActual: (val: boolean) => void;
}

const Filter: React.FC<FilterProps> = ({
    show,
    setShow,
    setFilteredModifications,
    setStartDate,
    setEndDate,
    startDate = '',
    endDate = '', 
    selectedUsers,
    setSelectedUsers,
    selectedMesas,
    setSelectedMesas,
    soloSesionActual,
    setSoloSesionActual
}) => {
  const [modifications, setModifications] = useState<PedidoModification[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UsersOption[]>([]);
  const [mesas, setMesas] = useState<MesasOption[]>([]);
  const [amountRange, setAmountRange] = useState<[number, number]>([0, 100000]);
  // Nuevo estado para activar/desactivar la sección de monto
  const [enableAmountRange, setEnableAmountRange] = useState(false);


  useEffect(() => {
    (async () => {
      const fetchedUsers = await getUsersOptions();
      const fetchedMesas = await getMesasOptions();
      setUsers(fetchedUsers);
      setMesas(fetchedMesas);
    })();
  }, []);

  useEffect(() => {
  const userContext = localStorage.getItem('user');
  const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
  const sessionId = userContext != null ? JSON.parse(userContext).local_session_id : 0;

    // If the user selected 'solo sesión actual' call the session-specific API
    // otherwise call the range-by-date API
    if (soloSesionActual && sessionId) {
      // API signature expects only localId; the backend will return current-session data
      getAllModificationsByLocalIdAndSessionId(localId)
        .then((response) => {
          const payload = Array.isArray(response.data) ? response.data : [];
          const reversed = payload.reverse();
          setModifications(reversed);
          setFilteredModifications(reversed);
        })
        .catch((error) => {
          console.error("Error fetching session modifications:", error);
        })
        .finally(() => setLoading(false));
    } else {
      getAllModificationsByLocalIdAndRangeDate(localId, startDate, endDate)
        .then((response) => {
          const payload = Array.isArray(response.data) ? response.data : [];
          const reversed = payload.reverse();
          setModifications(reversed);
          setFilteredModifications(reversed);
        })
        .catch((error) => {
          console.error("Error fetching pedidos:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [startDate, endDate, soloSesionActual]);

  useEffect(() => {
    let filtered = modifications;
    // Filtro por fecha usando startDate y endDate si soloSesionActual está desactivado
    if (!soloSesionActual && startDate && endDate) {
      // Convertir fechas a objetos Date para comparación
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');
      filtered = filtered.filter(mod => {
        if (!mod.timestamp) return false;
        // Parseo robusto del timestamp
        let dateObj;
        try {
          dateObj = new Date(mod.timestamp);
        } catch {
          return false;
        }
        if (isNaN(dateObj.getTime())) return false;
        return dateObj >= start && dateObj <= end;
      });
    } else if (soloSesionActual) {
      // No extra date filtering here — when soloSesionActual is active we already
      // fetched session-scoped modifications from the backend via the session API.
      // Keep the current `modifications` array as-is so user/mesa/amount filters apply.
      filtered = filtered;
    }
    filtered = filtered.filter(mod =>
      (selectedUsers.length === 0 || selectedUsers.some(o => String(o.value) === String(mod.user_id))) &&
      (selectedMesas.length === 0 || selectedMesas.some(o => String(o.value) === String(mod.mesa_id))) &&
      // Solo filtrar por monto si enableAmountRange está activo
      (!enableAmountRange || (mod.modification_amount >= amountRange[0] && mod.modification_amount <= amountRange[1]))
    );
    console.log('Filtered modifications:', filtered);
    setFilteredModifications(filtered);
  }, [modifications, selectedUsers, selectedMesas, soloSesionActual, startDate, endDate, amountRange, enableAmountRange]);

  const usersOptions = users.map(u => ({ value: u.id, label: u.name }));
  const mesasOptions = mesas.map(m => ({ value: m.id, label: m.num }));


  const handleUsersChange = (opts: any[]) => setSelectedUsers(opts);
  const handleMesasChange = (opts: any[]) => setSelectedMesas(opts);
  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    setSoloSesionActual(false);
  };
  const handleEndDateChange = (date: string) => {
    setEndDate(date);
    setSoloSesionActual(false);
  };


  const renderFilters = () => {
    return (
      <div className="usuarios-header">
        <MultiSelectFilter
          options={usersOptions}
          onChange={handleUsersChange}
          placeholder="Filtrar por usuarios"
        />
        <MultiSelectFilter
          options={mesasOptions}
          onChange={handleMesasChange}
          placeholder="Filtrar por mesas"
        />
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner text="Cargando Modificaciones" transparent={true} />;
  }

  return (
    <Offcanvas
      show={show}
      onHide={() => setShow(false)}
      placement="end"
      className="mp-offcanvas modificaciones-offcanvas"
      backdrop={true}
      scroll={false}
    >
      <Offcanvas.Header className="offcanvas-header">
        <div className="form-modal-title-container header-content">
          <Offcanvas.Title className="offcanvas-title">Opciones de Filtrado</Offcanvas.Title>
          <div className="section-indicator">Historial de Modificaciones</div>
        </div>

        <Button variant="none" className="close-button" onClick={() => setShow(false)} aria-label="Cerrar filtros">
          <X size={24} />
        </Button>
      </Offcanvas.Header>

      <Offcanvas.Body className="offcanvas-body">
        <div className="filters-container">
          <div className="filter-section">
            <h6 className="filter-section-title">Filtros Generales</h6>

            <div className="filter-group">
              <Form.Label className="filter-label">Rango de fechas</Form.Label>
              <div className="receipt-date-filter-container">
                <DateFilter
                  startDate={startDate}
                  setStartDate={handleStartDateChange}
                  endDate={endDate}
                  setEndDate={handleEndDateChange}
                />
              </div>
            </div>

            <div className="filter-group">
              <div className="multiselect-container">
                <MultiSelectFilter
                  options={usersOptions}
                  onChange={handleUsersChange}
                  placeholder="Filtrar por usuarios"
                />
              </div>

              <div className="multiselect-container" style={{ marginTop: '0.5rem' }}>
                <MultiSelectFilter
                  options={mesasOptions}
                  onChange={handleMesasChange}
                  placeholder="Filtrar por mesas"
                />
              </div>
            </div>

            <div className="filter-group">
              <div className="checkbox-filter">
                <Form.Check
                  type="checkbox"
                  id="enableAmountRangeMobile"
                  label="Activar rango por monto"
                  checked={enableAmountRange}
                  onChange={e => setEnableAmountRange(e.target.checked)}
                  className="mediopunto-form-check-input"
                />
              </div>
            </div>

            {enableAmountRange && (
              <div className="filter-group">
                <div className="mp-formgroup vertical amount_range">
                  <Form.Label>Rango de Monto</Form.Label>
                  <div className="d-flex justify-content-between">
                    <InputGroup className="mp-form-input-group">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={amountRange[0]}
                        onChange={e => setAmountRange([Number(e.target.value), amountRange[1]])}
                        placeholder="Mínimo"
                      />
                    </InputGroup>
                    <span className="mx-2">-</span>
                    <InputGroup className="mp-form-input-group">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={amountRange[1]}
                        onChange={e => setAmountRange([amountRange[0], Number(e.target.value)])}
                        placeholder="Máximo"
                      />
                    </InputGroup>
                  </div>
                </div>
              </div>
            )}

            <div className="filter-group">
              <div className="checkbox-filter">
                <input
                  type="checkbox"
                  id="soloSesionActualMobile"
                  checked={soloSesionActual}
                  onChange={e => setSoloSesionActual(e.target.checked)}
                />
                <label htmlFor="soloSesionActualMobile">Solo sesión actual</label>
              </div>
            </div>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default Filter;
